import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

export const NotificationsBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = items.filter((n) => !n.read).length;

  const load = useCallback(async () => {
    try { const r = await api.get("/me/notifications"); setItems(r.data); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const iv = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try { await api.post("/me/notifications/read"); } catch (e) { /* ignore */ }
      setItems((l) => l.map((n) => ({ ...n, read: true })));
    }
  };

  const openNotif = (n) => {
    const dest = n.link || (n.candle_id ? `/candle/${n.candle_id}` : null);
    if (!dest) return;
    setOpen(false);
    navigate(dest);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground" aria-label="Notifications" data-testid="notifications-bell">
          <Bell size={16} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-semibold text-white" data-testid="notifications-badge">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="glass w-80 border-white/10 p-0" data-testid="notifications-panel">
        <div className="border-b border-white/10 px-4 py-2 text-sm font-medium">{t("header.notifications")}</div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t("header.noNotifications")}</p>
          ) : items.map((n) => (
            <div
              key={n.id}
              onClick={() => openNotif(n)}
              className={`flex items-start gap-3 border-b border-white/5 px-4 py-3 last:border-0 transition-colors ${(n.link || n.candle_id) ? "cursor-pointer hover:bg-white/5" : ""}`}
              data-testid="notification-item"
            >
              {n.photo_url && (
                <img src={n.photo_url} alt={n.deceased_name || ""} className="mt-0.5 h-10 w-10 shrink-0 rounded-full border-2 border-amber-300/70 object-cover" data-testid="notification-photo" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-sky-300">{n.title}</p>
                <p className="mt-0.5 text-sm text-foreground/90">{n.message}</p>
                {(n.link || n.candle_id) && (
                  <p className="mt-1 text-[11px] font-medium text-amber-300" data-testid="notification-link">{t("header.viewCandle")}</p>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
